/**
 * Email deep-link parameter handling.
 *
 * Reads `?prompt=...&autoSend=1&source=...` from the URL, stashes it across
 * the Federate redirect via sessionStorage, and consumes it once the user is
 * authenticated. See features/email-deep-link-prompt.md for the full design.
 */

export interface EmailDeepLinkParams {
  prompt: string;
  autoSend: boolean;
  source?: string;
}

const MAX_PROMPT_LEN = 2000;
const SS_KEY = 'rocky_pending_deep_link';

export function readDeepLinkFromUrl(): EmailDeepLinkParams | null {
  const sp = new URLSearchParams(window.location.search);
  const prompt = sp.get('prompt');
  if (!prompt) return null;
  if (prompt.length > MAX_PROMPT_LEN) {
    console.warn('[deepLink] prompt exceeds max length, truncating');
  }
  return {
    prompt: prompt.slice(0, MAX_PROMPT_LEN),
    autoSend: sp.get('autoSend') === '1',
    source: sp.get('source') ?? undefined,
  };
}

export function stripDeepLinkFromUrl(): void {
  const url = new URL(window.location.href);
  ['prompt', 'autoSend', 'source'].forEach((k) => url.searchParams.delete(k));
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
}

export function stashDeepLink(p: EmailDeepLinkParams): void {
  sessionStorage.setItem(SS_KEY, JSON.stringify(p));
}

export function consumeDeepLink(): EmailDeepLinkParams | null {
  const raw = sessionStorage.getItem(SS_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(SS_KEY);
  try {
    return JSON.parse(raw) as EmailDeepLinkParams;
  } catch {
    return null;
  }
}
